import { useDebounce } from "../../../util/hooks";
import pick from "lodash/pick";
import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { Auth } from "../../../components/Auth";
import {
  CustomTermContainer,
  WarningMessage,
} from "../../../components/Form/Form";
import { InfoBlockSmall } from "../../../components/InfoBlocks/InfoBlocks";
import { Notifications } from "../../../components/Notifications/NotificationsContext";
import { SelectBoxV2 } from "../../../components/SelectBoxV2/SelectBoxV2";
import { formatTwoLineOptionLabel } from "../../../components/SelectBoxV2/TwoLineOptionLabel";
import { endpoints } from "../../../endpoints";
import type {
  OptionType,
  UUID,
  Tenant,
  ITenantCustomerSettings,
  DeliveryTermPaginatedOutput,
  PaymentTermPaginatedOutput,
  AgilisUsersPaginatedOutput,
} from "../../../types/types";
import {
  useStoreState,
  makeDeliveryTermsGetEndpoint,
  makePaymentTermsGetEndpoint,
  TEMPORARY_HIGH_LIMIT,
  convertDeliveryTermToOption,
  convertPaymentTermToOption,
  makeUrlWithParams,
  markDefaultTerm,
} from "../../../util/util";
import {
  newShippingAddress,
  useShippingPointOfContactField,
} from "../../../components/quoteCart/cartUtils";
import { useCountriesList } from "../../../util/Locations";
import type { BuyerOption } from "./CustomerDetailsForm.utils";
import {
  NEW_ADDRESS_VALUE,
  buyerUserToOption,
  filterPointOfContactOption,
  filterShippingAddressOption,
  shippingAddressToOption,
  shippingPointOfContactToOption,
} from "./CustomerDetailsForm.utils";

const keepAllOptions = () => true;

export type CustomerDetailsFormOutput = {
  buyer_id: BuyerOption;
  order_place_via: OptionType<string>;
  shipping_address_id: OptionType<UUID>;
  shipping_point_of_contact_id: OptionType<UUID>;
  delivery_term: OptionType<UUID>;
  payment_term: OptionType<UUID>;
};

type CustomerDetailsFormProps = {
  isDisabled: boolean;
  setSelectedBuyer: React.Dispatch<React.SetStateAction<Tenant | null>>;
  setCustomerSettings: React.Dispatch<
    React.SetStateAction<ITenantCustomerSettings | null>
  >;
  orderType?: "quote" | "order" | "sample";
};

export function CustomerDetailsForm({
  isDisabled,
  setSelectedBuyer,
  setCustomerSettings,
  orderType = "order",
}: CustomerDetailsFormProps) {
  const { control, errors, formState, watch, setValue, clearErrors } =
    useFormContext<CustomerDetailsFormOutput>();
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { user } = useContext(Auth);
  const { notifyError } = useContext(Notifications);
  const [buyerSearchQuery, setBuyerSearchQuery] =
    useState<string | undefined>();
  const [debouncedBuyerSearchQuery] = useDebounce(buyerSearchQuery, 1000);
  // const [buyerSettingsLocal, setBuyerSettingsLocal]
  const [defaultTerms, setDefaultTerms] =
    useState<Partial<ITenantCustomerSettings> | null>(null);

  const [isDefaultPaymentTerm, setIsDefaultPaymentTerm] = useState(true);
  const [isDefaultDeliveryTerm, setIsDefaultDeliveryTerm] = useState(true);

  /**
   * First we have to select the buyer name, from which we get the tenant ID. Then we
   * fetch the addreses based on that.
   */

  //  TODO: create useShippingTerms and usePaymentTerms hooks.

  const { data: fetchedShippingTerms } = useSWR<DeliveryTermPaginatedOutput>(
    //   TODO: do we use storefront_id of buyer or seller here?
    makeDeliveryTermsGetEndpoint(storefront_id),
    {
      onError: (error) => {
        notifyError(t("There was an error fetching the shipping terms."), {
          error,
        });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { data: fetchedPaymentTerms } = useSWR<PaymentTermPaginatedOutput>(
    makePaymentTermsGetEndpoint(storefront_id),
    {
      onError: (error) => {
        notifyError(t("There was an error fetching the payment terms."), {
          error,
        });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const countries = useCountriesList();

  const { data: fetchedBuyers, error: buyersError } =
    useSWR<AgilisUsersPaginatedOutput>(
      // TODO remove limit and make search only.
      makeUrlWithParams(endpoints.v1_storefronts_id_buyers(storefront_id), {
        limit: TEMPORARY_HIGH_LIMIT,
        q: debouncedBuyerSearchQuery,
      }),
      {
        onError: (error) => {
          notifyError(t("There was an error fetching the buyers"), {
            error,
          });
        },
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      }
    );

  // Results aren't filtered on the client, so until the debounced search has
  // gone out the menu would still offer the rows of the previous search.
  const isBuyerSearchPending = buyerSearchQuery !== debouncedBuyerSearchQuery;
  const buyersOptions = isBuyerSearchPending
    ? []
    : fetchedBuyers?.data.map(buyerUserToOption) ?? [];
  const isLoadingBuyers =
    isBuyerSearchPending || (!fetchedBuyers && !buyersError);

  const { buyer_id: buyerOption } = watch(["buyer_id"]);
  const buyerUserID = buyerOption?.value ?? null;
  // The buyer's company. Transactions, addresses and settings all hang off it.
  const selectedBuyerID = buyerOption?.tenant_id ?? null;

  const previousBuyerUserID = useRef(buyerUserID);
  useEffect(() => {
    if (previousBuyerUserID.current === buyerUserID) {
      return;
    }
    previousBuyerUserID.current = buyerUserID;
    setValue("shipping_address_id", null);
    setValue("shipping_point_of_contact_id", null);
  }, [buyerUserID, setValue]);

  const { data: fetchedCustomer, error: customerError } = useSWR<Tenant>(
    selectedBuyerID
      ? endpoints.v1_storefronts_id_tenants_id(storefront_id, selectedBuyerID)
      : null,
    {
      onError: (error) => {
        notifyError(t("There was an error fetching the customer information"), {
          error,
        });
      },
      // TODO replace with useNotifyOnce hook?
      shouldRetryOnError: false,
    }
  );

  const customer = selectedBuyerID ? fetchedCustomer : undefined;

  // Driven by the data rather than SWR's `onSuccess`, which is skipped when a
  // company is picked again inside the dedupe window and would leave the page
  // with no company, or with the previous one.
  useEffect(() => {
    setSelectedBuyer(customer ?? null);
  }, [customer, setSelectedBuyer]);

  const { data: fetchedCustomerSettings } = useSWR<ITenantCustomerSettings>(
    selectedBuyerID && user?.seller_id
      ? endpoints.v1_storefronts_id_tenants_id_customers_id_settings(
          storefront_id,
          user.tenant_id,
          selectedBuyerID
        )
      : null,
    {
      onError: (error) => {
        notifyError("There was an error fetching buyer storefront settings", {
          error,
        });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const customerSettings = selectedBuyerID
    ? fetchedCustomerSettings
    : undefined;

  useEffect(() => {
    if (!customerSettings) {
      return;
    }
    // we need this data on two levels. This is where something like zustand
    // would be nice.
    const obj = pick(customerSettings, [
      "default_delivery_term",
      "default_payment_term",
      "default_payment_mode",
    ]);
    setValue(
      "delivery_term",
      customerSettings.default_delivery_term
        ? convertDeliveryTermToOption(customerSettings.default_delivery_term)
        : null
    );
    setValue(
      "payment_term",
      customerSettings.default_payment_term
        ? convertPaymentTermToOption(customerSettings.default_payment_term)
        : null
    );
    setDefaultTerms(obj);
    setCustomerSettings(customerSettings);
  }, [customerSettings, setValue, setCustomerSettings]);

  const watchedTerms = watch(["delivery_term", "payment_term"]);

  useEffect(() => {
    if (defaultTerms) {
      if (
        defaultTerms.default_delivery_term?.id ===
        watchedTerms?.delivery_term?.value
      ) {
        setIsDefaultDeliveryTerm(true);
      } else if (
        defaultTerms.default_delivery_term?.id !==
          watchedTerms?.delivery_term?.value &&
        defaultTerms.default_delivery_term !== null
      ) {
        setIsDefaultDeliveryTerm(false);
      }

      if (
        defaultTerms.default_payment_term?.id ===
        watchedTerms?.payment_term?.value
      ) {
        setIsDefaultPaymentTerm(true);
      } else if (
        defaultTerms.default_payment_term?.id !==
          watchedTerms.payment_term?.value &&
        defaultTerms.default_payment_term !== null
      ) {
        setIsDefaultPaymentTerm(false);
      }
    }
  }, [defaultTerms, watchedTerms]);

  const customerName = customer?.name;
  const isLoadingCustomer = !fetchedCustomer && !customerError;

  // The server already leaves out inactive locations; `is_active` is a safeguard.
  const allShippingAddresses = useMemo(
    () =>
      customer?.addresses?.filter(
        (a) => a.type === "Warehouse" && a.is_active
      ) ?? [],
    [customer]
  );

  const addressOptions = useMemo(
    () =>
      customer
        ? [
            ...allShippingAddresses.map((address) =>
              shippingAddressToOption(address, countries, t)
            ),
            newShippingAddress(t),
          ]
        : [],
    [customer, allShippingAddresses, countries, t]
  );

  const { shipping_address_id: selectedShippingAddressOption } = watch([
    "shipping_address_id",
  ]);
  const selectedShippingAddress =
    selectedShippingAddressOption?.value === NEW_ADDRESS_VALUE
      ? undefined
      : allShippingAddresses.find(
          (a) => a.id === selectedShippingAddressOption?.value
        );
  const { options: pointOfContactOptions } = useShippingPointOfContactField({
    address: selectedShippingAddress,
    fieldName: "shipping_point_of_contact_id",
    setValue,
    toOption: shippingPointOfContactToOption,
  });

  // The hook above refills or empties the POC with a plain `setValue`, which
  // leaves a "required" error from an earlier submit attempt in place.
  useEffect(() => {
    clearErrors("shipping_point_of_contact_id");
  }, [selectedShippingAddressOption?.value, clearErrors]);

  // const prefilledDestinationOption = { value: "prefilled", label: "prefilled" };

  const OrderPlacedViaOptions = [
    { value: "Email", label: t("Email") },
    { value: "Fax", label: t("Fax") },
    { value: "Phone", label: t("Phone") },
    { value: "Text", label: t("Text") },
  ];

  const shippingTermsOptions =
    fetchedShippingTerms?.data.map(convertDeliveryTermToOption) ?? [];
  const paymentTermsOptions =
    fetchedPaymentTerms?.data.map(convertPaymentTermToOption) ?? [];

  const handleDeliveryTermChange = (value: any) => {
    if (value.value !== defaultTerms?.default_delivery_term?.id) {
    }
  };

  const handleSearchBuyerInput = (value: string) => {
    value === "" ? setBuyerSearchQuery(undefined) : setBuyerSearchQuery(value);
  };

  // First get buyer tenant id, to fetch shipping addresses.
  return (
    <>
      {/* TODO: use paginated select search bar. */}
      <Controller
        as={SelectBoxV2}
        isSearchBar
        control={control}
        name="buyer_id"
        placeholder={t("Buyer Name")}
        options={buyersOptions}
        onInputChange={handleSearchBuyerInput}
        // The server searches by name, email and company; filtering on the
        // label here would hide its company-name matches.
        filterOption={keepAllOptions}
        formatOptionLabel={formatTwoLineOptionLabel}
        isLoading={isLoadingBuyers}
        isClearable
        defaultValue={null}
        isDisabled={isDisabled}
        rules={{ required: true }}
        errors={errors}
        formState={formState}
      />
      <InfoBlockSmall
        header={t("Buyer's Company")}
        content={
          customerName
            ? customerName
            : !selectedBuyerID
            ? "--"
            : isLoadingCustomer
            ? // Avoid flash of "--" when switching buyers in input above.
              ""
            : // Shouldn't get here.
              "--"
        }
      />
      <Controller
        as={SelectBoxV2}
        control={control}
        name="order_place_via"
        placeholder={
          orderType === "quote"
            ? t("Quote Requested via")
            : orderType === "sample"
            ? t("Sample Requested via")
            : t("Order placed via")
        }
        options={OrderPlacedViaOptions}
        defaultValue={null}
        isDisabled={isDisabled}
        rules={{ required: orderType === "order" }}
        errors={errors}
        formState={formState}
      />
      <Controller
        as={SelectBoxV2}
        control={control}
        name="shipping_address_id"
        placeholder={t("Shipping Address")}
        options={addressOptions}
        filterOption={filterShippingAddressOption}
        formatOptionLabel={formatTwoLineOptionLabel}
        isClearable
        defaultValue={null}
        isDisabled={isDisabled || !buyerOption}
        rules={{ required: true }}
        errors={errors}
        formState={formState}
      />
      <Controller
        as={SelectBoxV2}
        control={control}
        name="shipping_point_of_contact_id"
        placeholder={t("Point of Contact")}
        options={pointOfContactOptions}
        filterOption={filterPointOfContactOption}
        formatOptionLabel={formatTwoLineOptionLabel}
        defaultValue={null}
        // Stays disabled for "New Address" (its POC comes from the inline
        // address form) and for single-POC locations (auto-filled).
        isDisabled={isDisabled || pointOfContactOptions.length <= 1}
        // A disabled Controller still validates in react-hook-form v6.
        rules={{ required: pointOfContactOptions.length > 0 }}
        errors={errors}
        formState={formState}
      />
      {/* TODO turn these into components on their own  */}
      <CustomTermContainer
        highlight={!isDefaultDeliveryTerm}
        has_error={!!errors.delivery_term}
      >
        <Controller
          as={SelectBoxV2}
          control={control}
          name="delivery_term"
          placeholder={t("Shipping Terms")}
          onChange={handleDeliveryTermChange}
          options={
            defaultTerms?.default_delivery_term
              ? markDefaultTerm(
                  shippingTermsOptions,
                  convertDeliveryTermToOption(
                    defaultTerms.default_delivery_term
                  )
                )
              : shippingTermsOptions
          }
          isDisabled={isDisabled}
          rules={{ required: true }}
          errors={errors}
          formState={formState}
          setDefaultTerm={!!defaultTerms?.default_delivery_term}
        />
        {!isDefaultDeliveryTerm && (
          <WarningMessage>
            {t("Requested shipping terms are not the default terms")}
          </WarningMessage>
        )}
      </CustomTermContainer>

      <CustomTermContainer
        highlight={!isDefaultPaymentTerm}
        has_error={!!errors.payment_term}
      >
        <Controller
          as={SelectBoxV2}
          control={control}
          name="payment_term"
          placeholder={t("Payment Terms")}
          options={
            defaultTerms?.default_payment_term
              ? markDefaultTerm(
                  paymentTermsOptions,
                  convertPaymentTermToOption(defaultTerms.default_payment_term)
                )
              : paymentTermsOptions
          }
          isDisabled={isDisabled}
          rules={{ required: true }}
          errors={errors}
          formState={formState}
          setDefaultTerm={!!defaultTerms?.default_payment_term}
        />
        {!isDefaultPaymentTerm && (
          <WarningMessage>
            {t("Requested payment terms are not the default terms")}
          </WarningMessage>
        )}
      </CustomTermContainer>
    </>
  );
}
